import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { cccdAdmin, type AdminRegionalEventDetail, type CccdTeamStatus, type SeedbedOption } from '@/lib/api/cccdAdminClient';
import { TeamStatusBadge } from '@/components/cccd/TeamStatusBadge';
import { PodiumResultsSection } from '@/components/cccd/PodiumResultsSection';
import { EventCategoriesSection } from '@/components/cccd/EventCategoriesSection';
import { EventPlansSection } from '@/components/cccd/EventPlansSection';
import { EventCatalogItemsSection } from '@/components/cccd/EventCatalogItemsSection';
import { EventSponsorsSection } from '@/components/cccd/EventSponsorsSection';
import { ScoringResultsGrid } from '@/components/cccd/ScoringResultsGrid';
import { ScoringLeaderboard } from '@/components/cccd/ScoringLeaderboard';
import { LaunchControlSection } from '@/components/cccd/LaunchControlSection';
import { ProjectionButtons } from '@/components/cccd/ProjectionButtons';
import { RegionalShell, type RegionalTabKey } from '@/components/cccd/RegionalShell';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import { StatusPill } from '@/components/cccd/StatusPill';
import { EventActiveToggle } from '@/components/cccd/EventActiveToggle';
import { TeamsListCard } from '@/components/cccd/TeamsListCard';
import { RowActionsMenu } from '@/components/admin/RowActionsMenu';
import Alert from '@/components/ui/alert/Alert';
import { type DataTableColumn, type DataTableSort } from '@/components/ui/table/DataTable';
import { inputCls, labelCls, textareaCls, btnSave, btnSecondary } from '@/components/admin/FormComponents';
import { EyeIcon, CheckCircleIcon, CloseIcon } from '@/icons';
import { applyOptimistic, rollbackOptimistic } from '@/lib/optimisticMutation';
import InlineSavingChip from '@/components/ui/badge/InlineSavingChip';
import { getValidationErrorMessage } from '@/lib/errors';
import { toastError } from '@/lib/toast';
import { durationPill, registrationWindowPill } from '@/lib/cccdDatePills';
import { buildEventStatMetrics } from '@/lib/cccdEventMetrics';
import { toCsv, downloadCsv } from '@/lib/csv';
import { teamCsvTemplateHeaders } from '@/lib/teamsCsvImport';
import { TEAMS_PAGE_SIZE, sortTeams } from '@/lib/cccdTeamStatus';
import { LoadState } from "@/components/ui/LoadState"

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
}

function toDateTimeLocal(iso: string): string {
  return new Date(iso).toISOString().slice(0, 16);
}

function RegionalInfoForm({ regional, onSaved }: { regional: AdminRegionalEventDetail; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: regional.name,
    region: regional.region,
    description: regional.description,
    eventStartDate: toDateTimeLocal(regional.eventStartDate),
    eventEndDate: toDateTimeLocal(regional.eventEndDate),
    registrationStartDate: toDateTimeLocal(regional.registrationStartDate),
    registrationDeadline: toDateTimeLocal(regional.registrationDeadline),
    consentText: regional.consentText,
  });
  const [error, setError] = useState('');

  useEffect(() => {
    setForm({
      name: regional.name,
      region: regional.region,
      description: regional.description,
      eventStartDate: toDateTimeLocal(regional.eventStartDate),
      eventEndDate: toDateTimeLocal(regional.eventEndDate),
      registrationStartDate: toDateTimeLocal(regional.registrationStartDate),
      registrationDeadline: toDateTimeLocal(regional.registrationDeadline),
      consentText: regional.consentText,
    });
  }, [regional]);

  const saveMutation = useMutation({
    mutationFn: () =>
      cccdAdmin.updateRegional(regional.id, {
        name: form.name,
        region: form.region,
        description: form.description,
        eventStartDate: new Date(form.eventStartDate).toISOString(),
        eventEndDate: new Date(form.eventEndDate).toISOString(),
        registrationStartDate: new Date(form.registrationStartDate).toISOString(),
        registrationDeadline: new Date(form.registrationDeadline).toISOString(),
        consentText: form.consentText,
      }),
    onSuccess: () => onSaved(),
    onError: (err) => {
      const message = getValidationErrorMessage(err);
      setError(message);
      toastError(message);
    },
  });

  const f = (key: keyof typeof form) => ({
    value: form[key],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value })),
  });

  const eventDuration = durationPill(form.eventStartDate, form.eventEndDate);
  const registrationWindow = registrationWindowPill(form.registrationStartDate, form.registrationDeadline);

  return (
    <form onSubmit={(e) => { e.preventDefault(); setError(''); saveMutation.mutate(); }} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionHeader
          title="Identificación"
          subtitle="Nombre y región de este evento regional"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M4 4h16v16H4z" stroke="currentColor" strokeWidth="1.8" /><path d="M4 10h16M9 4v6" stroke="currentColor" strokeWidth="1.8" /></svg>}
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Nombre del regional *</label>
              <input required className={inputCls} {...f('name')} />
            </div>
            <div>
              <label className={labelCls}>Región *</label>
              <input required className={inputCls} {...f('region')} />
            </div>
          </div>
        </SectionHeader>

        <SectionHeader
          title="Fechas del concurso"
          subtitle="Cuándo se corre el regional presencialmente"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" /></svg>}
          badge={eventDuration && <StatusPill tone={eventDuration.tone} label={eventDuration.label} />}
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Inicio del evento *</label>
              <input required type="datetime-local" className={inputCls} {...f('eventStartDate')} />
            </div>
            <div>
              <label className={labelCls}>Fin del evento *</label>
              <input required type="datetime-local" className={inputCls} {...f('eventEndDate')} />
            </div>
          </div>
        </SectionHeader>

        <SectionHeader
          title="Inscripciones"
          subtitle="Ventana en la que los equipos pueden registrarse"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" /></svg>}
          badge={registrationWindow && <StatusPill tone={registrationWindow.tone} label={registrationWindow.label} />}
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Apertura de inscripciones *</label>
              <input required type="datetime-local" className={inputCls} {...f('registrationStartDate')} />
            </div>
            <div>
              <label className={labelCls}>Cierre de inscripciones *</label>
              <input required type="datetime-local" className={inputCls} {...f('registrationDeadline')} />
            </div>
          </div>
        </SectionHeader>

        <SectionHeader
          title="Descripción"
          subtitle="Cómo se presenta este regional a los equipos"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M4 4h16v16H4z" stroke="currentColor" strokeWidth="1.8" /><path d="M4 10h16M9 4v6" stroke="currentColor" strokeWidth="1.8" /></svg>}
        >
          <textarea required rows={2} className={textareaCls} {...f('description')} />
        </SectionHeader>

        <SectionHeader
          title="Consentimiento"
          subtitle="Texto legal que el equipo debe aceptar al inscribirse"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" /></svg>}
          className="lg:col-span-2"
        >
          <textarea required rows={3} className={textareaCls} {...f('consentText')} />
        </SectionHeader>
      </div>

      {error && <Alert variant="error" title="No se pudo guardar" message={error} />}
      <div className="flex justify-end">
        <button type="submit" disabled={saveMutation.isPending} className={btnSave}>
          {saveMutation.isPending ? 'Guardando...' : 'Guardar información'}
        </button>
      </div>
    </form>
  );
}

function ConfirmPaymentInlineForm({
  teamId,
  onCancel,
  onSuccess,
}: {
  teamId: string;
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const [bankReference, setBankReference] = useState('');
  const [receiptUrl, setReceiptUrl] = useState('');
  const [notes, setNotes] = useState('');

  // Pesimista a propósito, no pendiente: confirma un pago — mutación
  // financiera, excluida del patrón optimistic UI (ver design.md).
  const mutation = useMutation({
    mutationFn: () => cccdAdmin.confirmRegionalPayment(teamId, {
      bankReference: bankReference || undefined,
      receiptUrl: receiptUrl || undefined,
      notes: notes || undefined,
    }),
    onSuccess,
  });

  return (
    <div className="space-y-3 rounded-lg border border-success-200 bg-success-50/60 p-4 dark:border-success-800 dark:bg-success-900/10">
      <div>
        <label className={labelCls}>Referencia bancaria</label>
        <input
          type="text"
          value={bankReference}
          onChange={(e) => setBankReference(e.target.value)}
          placeholder="Número de referencia de la transferencia"
          className={inputCls}
        />
      </div>
      <div>
        <label className={labelCls}>URL del comprobante (opcional)</label>
        <input
          type="url"
          value={receiptUrl}
          onChange={(e) => setReceiptUrl(e.target.value)}
          placeholder="https://..."
          className={inputCls}
        />
      </div>
      <div>
        <label className={labelCls}>Notas (opcional)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className={textareaCls}
        />
      </div>
      {mutation.isError && (
        <Alert
          variant="error"
          title="No se pudo confirmar el pago"
          message="Verifica los datos e intenta nuevamente."
        />
      )}
      <div className="flex justify-end gap-3 pt-1">
        <button type="button" onClick={onCancel} className={`min-h-11 ${btnSecondary}`}>
          Cancelar
        </button>
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
          className="min-h-11 rounded-xl bg-success-600 px-4 py-2 text-sm font-medium text-white hover:bg-success-700 disabled:opacity-60"
        >
          {mutation.isPending ? 'Confirmando…' : 'Confirmar pago'}
        </button>
      </div>
    </div>
  );
}

function RegionalSeedbedSection({ regional }: { regional: AdminRegionalEventDetail }) {
  const qc = useQueryClient();
  const [seedbedId, setSeedbedId] = useState(regional.seedbedId ?? '');

  const { data: seedbedOptions = [], isError: seedbedsFailed } = useQuery<SeedbedOption[]>({
    queryKey: ['seedbed-options'],
    queryFn: () => cccdAdmin.listSeedbedOptions(),
  });

  // Sugerencia de semillero (cccd-seedbed-team-sync): solo mientras no hay
  // semillero elegido, buscando el regional más reciente con la misma región.
  const { data: seedbedSuggestion } = useQuery({
    queryKey: ['regional-seedbed-suggestion', regional.region],
    queryFn: () => cccdAdmin.suggestRegionalSeedbed(regional.region),
    enabled: !seedbedId,
  });

  // Toggle de detalle — aplica de inmediato, revierte si el servidor rechaza.
  // Ver design.md, receta "Toggle de detalle".
  const mutation = useMutation({
    mutationFn: (input: { seedbedId: string | null }) =>
      cccdAdmin.updateRegional(regional.id, input),
    onMutate: async (input) => {
      const previous = await applyOptimistic<AdminRegionalEventDetail>(qc, ['regional', regional.id], (prev) =>
        prev ? { ...prev, seedbedId: input.seedbedId } : prev,
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      rollbackOptimistic(qc, ['regional', regional.id], context?.previous);
      toastError('No se pudo guardar la configuración del regional.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['regional', regional.id] }),
  });
  const chipStatus = mutation.isPending ? 'saving' : mutation.isError ? 'error' : 'idle';

  return (
    <SectionHeader
      title="Semillero"
      subtitle="A qué semillero se inscriben automáticamente los miembros al activar su equipo"
      icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M12 3l8 4-8 4-8-4 8-4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M4 11v4c0 1.5 3.5 3 8 3s8-1.5 8-3v-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>}
    >
      <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className={labelCls}>Semillero (acceso a cursos)</label>
          <select
            value={seedbedId}
            onChange={(e) => setSeedbedId(e.target.value)}
            disabled={seedbedsFailed}
            className={`${inputCls} min-w-[220px]`}
          >
            <option value="">Sin semillero asociado</option>
            {regional.seedbedId && seedbedsFailed && (
              <option value={regional.seedbedId}>Semillero actual (no se pudo cargar el nombre)</option>
            )}
            {seedbedOptions.map((sb) => (
              <option key={sb.id} value={sb.id}>{sb.name}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => mutation.mutate({ seedbedId: seedbedId || null })}
          disabled={seedbedsFailed}
          className={`min-h-11 ${btnSave}`}
        >
          Guardar
        </button>
        <InlineSavingChip
          status={chipStatus}
          onRetry={() => mutation.mutate({ seedbedId: seedbedId || null })}
        />
      </div>
      {seedbedSuggestion && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-500/10 dark:text-brand-300">
          <span>
            Sugerencia: usar <strong>{seedbedSuggestion.seedbedName ?? 'este semillero'}</strong>, usado en «{seedbedSuggestion.sourceRegionalName}»
          </span>
          <button
            type="button"
            onClick={() => setSeedbedId(seedbedSuggestion.seedbedId)}
            className="min-h-11 shrink-0 rounded-md border border-brand-300 px-2 py-1 font-medium hover:bg-brand-100 dark:border-brand-700 dark:hover:bg-brand-500/20"
          >
            Usar
          </button>
        </div>
      )}
      {seedbedsFailed && (
        <Alert
          variant="error"
          title="No se pudo cargar la lista de semilleros"
          message="No tienes permiso para ver los semilleros o hubo un error de red. La configuración no se puede editar hasta resolverlo — el semillero actual no se muestra por seguridad."
        />
      )}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        El semillero define a qué cursos se inscriben automáticamente los miembros al activar su equipo.
      </p>
      </div>
    </SectionHeader>
  );
}

export default function RegionalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<RegionalTabKey>('info');
  const [statusFilter, setStatusFilter] = useState<CccdTeamStatus | 'all'>('all');
  const [confirmingTeamId, setConfirmingTeamId] = useState<string | null>(null);
  const [teamsSort, setTeamsSort] = useState<DataTableSort>({ column: 'createdAt', dir: 'desc' });
  const [teamsPage, setTeamsPage] = useState(1);

  const { data: regional, isLoading } = useQuery<AdminRegionalEventDetail>({
    queryKey: ['regional', id],
    queryFn: () => cccdAdmin.getRegional(id!),
    enabled: !!id,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['event-categories', 'regional', id],
    queryFn: () => cccdAdmin.getRegionalCategories(id!),
    enabled: !!id && (tab === 'podio' || tab === 'puntaje' || tab === 'clasificacion' || tab === 'lanzamiento'),
  });

  const sortedTeams = useMemo(() => {
    const teams = regional && statusFilter !== 'all' ? regional.teams.filter((t) => t.status === statusFilter) : regional?.teams ?? [];
    return sortTeams(teams, teamsSort);
  }, [regional, statusFilter, teamsSort]);
  const pagedTeams = sortedTeams.slice((teamsPage - 1) * TEAMS_PAGE_SIZE, teamsPage * TEAMS_PAGE_SIZE);
  const confirmingTeam = confirmingTeamId ? regional?.teams.find((t) => t.id === confirmingTeamId) ?? null : null;

  function onStatusFilterChange(s: CccdTeamStatus | 'all') {
    setStatusFilter(s);
    setTeamsPage(1);
  }

  function onDownloadTemplate() {
    const headers = teamCsvTemplateHeaders(false);
    const exampleRow = ['Equipo Ejemplo', 'Colegio Ejemplo', '', '', 'no'];
    downloadCsv(`equipos-plantilla-${regional?.slug ?? id}.csv`, toCsv([headers, exampleRow]));
  }

  // Toggle de detalle — aplica de inmediato, revierte si el servidor rechaza.
  // Mismo patrón que EventDetailPage, para que el header se vea idéntico.
  const toggleActiveMutation = useMutation({
    mutationFn: (nextIsActive: boolean) => cccdAdmin.updateRegional(id!, { isActive: nextIsActive }),
    onMutate: async (nextIsActive) => {
      const previous = await applyOptimistic<AdminRegionalEventDetail>(qc, ['regional', id], (prev) =>
        prev ? { ...prev, isActive: nextIsActive } : prev,
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      rollbackOptimistic(qc, ['regional', id], context?.previous);
      toastError('No se pudo actualizar el estado del regional.');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['regional', id] });
      qc.invalidateQueries({ queryKey: ['regionals'] });
    },
  });
  const toggleActiveChipStatus = toggleActiveMutation.isPending ? 'saving' : toggleActiveMutation.isError ? 'error' : 'idle';

  // cccd-team-showcase: ídem EventDetailPage — destaca este regional para
  // Competidores en Academy, exclusivo entre regionales (backend lo aplica).
  const toggleFeaturedMutation = useMutation({
    mutationFn: () => cccdAdmin.updateRegional(id!, { isFeaturedInAcademy: true }),
    onError: () => toastError('No se pudo destacar el regional en Academy.'),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['regional', id] });
      qc.invalidateQueries({ queryKey: ['regionals'] });
    },
  });
  const toggleFeaturedChipStatus = toggleFeaturedMutation.isPending ? 'saving' : toggleFeaturedMutation.isError ? 'error' : 'idle';

  // cccd-event-attendance-tickets: ídem EventDetailPage, para regionales.
  const toggleAttendeeTicketsMutation = useMutation({
    mutationFn: (next: boolean) => cccdAdmin.toggleRegionalAttendeeTickets(id!, next),
    onError: () => toastError('No se pudo actualizar el estado de los boletos de asistencia.'),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['regional', id] });
      qc.invalidateQueries({ queryKey: ['regionals'] });
    },
  });
  const toggleAttendeeTicketsChipStatus = toggleAttendeeTicketsMutation.isPending
    ? 'saving'
    : toggleAttendeeTicketsMutation.isError
      ? 'error'
      : 'idle';

  const teamColumns: DataTableColumn<AdminRegionalEventDetail['teams'][number]>[] = [
    { key: 'name', label: 'Equipo', sortable: true, render: (team) => <span className="font-medium text-gray-900 dark:text-white">{team.name}</span> },
    {
      key: 'captain',
      label: 'Capitán',
      render: (team) => team.captain ? (
        <>
          <div>{team.captain.name}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{team.captain.email}</div>
        </>
      ) : (
        <span className="text-xs text-gray-500 dark:text-gray-400">Sin responsable</span>
      ),
    },
    { key: 'members', label: 'Miembros', render: (team) => team._count.members },
    { key: 'status', label: 'Estado', sortable: true, render: (team) => <TeamStatusBadge status={team.status} /> },
    { key: 'createdAt', label: 'Creado', sortable: true, render: (team) => <span className="text-gray-500">{formatDate(team.createdAt)}</span> },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'right',
      render: (team) => {
        const hasPendingPayment = team.payments.some((p) => p.status === 'pending' && p.paymentMethod === 'bank_transfer_national');
        return (
          <div className="flex justify-end">
            <RowActionsMenu
              actions={[
                { label: 'Ver equipo', icon: <EyeIcon />, to: `/cccd/teams/${team.id}` },
                // cccd-decouple-payment-from-activation: registrar un pago ya no
                // depende de que el equipo siga sin activar — un equipo activo
                // puede seguir recibiendo pagos (ej. cuotas).
                ...(hasPendingPayment
                  ? [
                      confirmingTeamId === team.id
                        ? { label: 'Cerrar formulario de pago', icon: <CloseIcon />, onClick: () => setConfirmingTeamId(null), requires: 'cccd:addons:edit' as const }
                        : { label: 'Confirmar pago', icon: <CheckCircleIcon />, onClick: () => setConfirmingTeamId(team.id), requires: 'cccd:addons:edit' as const },
                    ]
                  : []),
              ]}
            />
          </div>
        );
      },
    },
  ];

  return (
    <RegionalShell
      mode="edit"
      activeTab={tab}
      onTabChange={setTab}
      breadcrumbs={[{ label: 'SateLab', to: '/' }, { label: 'CCCD', to: '/cccd/regionals' }, regional?.name ?? '...']}
      title={regional?.name ?? 'Cargando...'}
      subtitle={regional?.region ?? 'Concurso regional CCCD'}
      metrics={regional ? buildEventStatMetrics(regional, 'Equipos registrados en este regional') : []}
      headerActions={
        regional ? (
          <>
            <ProjectionButtons id={regional.id} kind="regional" />
            <EventActiveToggle
              isActive={regional.isActive}
              onToggle={(next) => toggleActiveMutation.mutate(next)}
              status={toggleActiveChipStatus}
              isFeaturedInAcademy={regional.isFeaturedInAcademy}
              onToggleFeatured={() => toggleFeaturedMutation.mutate()}
              featuredStatus={toggleFeaturedChipStatus}
              attendeeTicketsEnabled={regional.attendeeTicketsEnabled}
              onToggleAttendeeTickets={(next) => toggleAttendeeTicketsMutation.mutate(next)}
              attendeeTicketsStatus={toggleAttendeeTicketsChipStatus}
            />
          </>
        ) : undefined
      }
    >
      {isLoading ? (
        <LoadState />
      ) : !regional ? (
        <Alert variant="error" title="Regional no encontrado" message="No se encontró el evento regional solicitado." />
      ) : (
        <>
          {tab === 'info' && (
            <RegionalInfoForm regional={regional} onSaved={() => qc.invalidateQueries({ queryKey: ['regional', id] })} />
          )}

          {tab === 'equipos' && (
            <div className="space-y-5">
              <TeamsListCard
                columns={teamColumns}
                rows={pagedTeams}
                rowKey={(team) => team.id}
                statusFilter={statusFilter}
                onStatusFilterChange={onStatusFilterChange}
                onCreateTeam={() => navigate(`/cccd/teams/new?regionalId=${id}`)}
                onDownloadTemplate={onDownloadTemplate}
                onImportTeams={() => navigate(`/cccd/teams/import?regionalId=${id}`)}
                sort={teamsSort}
                onSortChange={setTeamsSort}
                page={teamsPage}
                pageSize={TEAMS_PAGE_SIZE}
                total={sortedTeams.length}
                onPageChange={setTeamsPage}
              />

              {confirmingTeam && (
                <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] space-y-3">
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                    Confirmar pago — {confirmingTeam.name}
                  </h3>
                  <ConfirmPaymentInlineForm
                    teamId={confirmingTeam.id}
                    onCancel={() => setConfirmingTeamId(null)}
                    onSuccess={() => {
                      setConfirmingTeamId(null);
                      qc.invalidateQueries({ queryKey: ['regional', id] });
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {tab === 'configuracion' && (
            <DndProvider backend={HTML5Backend}>
              <div className="space-y-10">
                <EventCategoriesSection parent={{ type: 'regional', regionalId: id! }} />
                <EventPlansSection parent={{ type: 'regional', regionalId: id! }} />
                <EventCatalogItemsSection eventId={id!} />
                <EventSponsorsSection parent={{ type: 'regional', id: id! }} />
                <RegionalSeedbedSection regional={regional} />
              </div>
            </DndProvider>
          )}

          {tab === 'puntaje' && (
            <ScoringResultsGrid categories={categories} />
          )}

          {tab === 'clasificacion' && (
            <ScoringLeaderboard categories={categories} />
          )}

          {tab === 'lanzamiento' && (
            <DndProvider backend={HTML5Backend}>
              <LaunchControlSection categories={categories} />
            </DndProvider>
          )}

          {tab === 'podio' && (
            <PodiumResultsSection
              target={{ type: 'regional', regionalId: regional.id, categories: categories.map((c) => ({ slug: c.slug, name: c.name })) }}
              teams={regional.teams.map((t) => ({ id: t.id, name: t.name }))}
            />
          )}
        </>
      )}
    </RegionalShell>
  );
}
